import clsx from 'clsx';
import React from 'react';
import { useParams } from 'react-router-dom';
import LoanPropertyContainer from 'src/components/loanOfficer/Loan/Forms/Residential/LoanInfo/LoanProperty/PropertyContainer';
import {
  ConnectedAddressInput,
  ConnectedCurrencyInput,
  ConnectedRadioInput,
  ConnectedPercentInput,
  ConnectedDateInput,
  ConnectedPlainInput,
  AutosavingFormProvider,
} from 'src/components/ConnectedInputs';
import { useIsLoanReadonly, useLoan, useLoanProperty } from 'src/hooks';
import {
  FNF_PROPERTY_TYPE_OPTIONS,
  LOAN_PURPOSE_OPTIONS_ALL,
  PROPERTYUSAGETYPE_OPTIONS,
  SUBMIT_MODE,
} from 'src/util';
import styles from 'src/components/loanOfficer/Loan/form.module.scss';
import FieldLockDataToggle from 'src/components/baseComponents/Inputs/InputAddons/FieldLockDataToggle';
import { useForm } from 'react-hook-form';
import { Paper } from '@mui/material';
import { updateLoan } from 'src/actions';
import { useDispatch } from 'react-redux';

// eslint-disable-next-line complexity
const ClosedSterlingBrokerProperty = () => {
  const dispatch = useDispatch();
  const { loanGuid } = useParams();
  const loanProperty = useLoanProperty(loanGuid);

  const isRefinance = loanProperty.loanPurposeType
    ?.toLowerCase()
    .includes('refi');

  const isConstruction = loanProperty.loanPurposeType
    ?.toLowerCase()
    .includes('construction');
  const disabled = useIsLoanReadonly(loanGuid);
  const loan = useLoan(loanGuid);
  const fieldLockData = loan.fieldLockData ?? [];
  const formMethods = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      property: {
        addressLineText: loanProperty.addressLineText ?? '',
        city: loanProperty.city ?? '',
        county: loanProperty.county ?? '',
        state: loanProperty.state ?? '',
        postalCode: loanProperty.postalCode ?? '',
        loanPurposeType: loanProperty.loanPurposeType ?? '',
        refinancePropertyAcquiredYear:
          loanProperty.refinancePropertyAcquiredYear ?? '',
        refinancePropertyOriginalCostAmount:
          loanProperty.refinancePropertyOriginalCostAmount ?? '',
        refinancePropertyExistingLienAmount:
          loanProperty.refinancePropertyExistingLienAmount ?? '',
        financedNumberOfUnits: loanProperty.financedNumberOfUnits ?? '',
      },
      customFieldsMapped: {
        rtl: {
          valueAsIs: loan.customFieldsMapped?.rtl?.valueAsIs ?? '',
          renovationCost: loan.customFieldsMapped?.rtl?.renovationCost ?? '',
          afterRenovationValue:
            loan.customFieldsMapped?.rtl?.afterRenovationValue ?? '',
          dayOneLoanAmount:
            loan.customFieldsMapped?.rtl?.dayOneLoanAmount ?? '',
          disbursedHoldbackAmount:
            loan.customFieldsMapped?.rtl?.disbursedHoldbackAmount ?? '',
          propertyType: loan.customFieldsMapped?.rtl?.propertyType ?? '',
        },
        sterling: {
          closedTotalFees:
            loan.customFieldsMapped?.sterling?.closedTotalFees ?? '',
          closedBrokerBankName:
            loan.customFieldsMapped?.sterling?.closedBrokerBankName ?? '',
        },
      },
      propertyUsageType: loan.applications?.[0]?.propertyUsageType ?? '',
      purchasePriceAmount: loan.purchasePriceAmount ?? '',
      borrowerRequestedLoanAmount: loan.borrowerRequestedLoanAmount ?? '',
      requestedInterestRatePercent: loan.requestedInterestRatePercent ?? '',
    },
  });

  const handleSave = async (data) => {
    const loanPatch = { ...data };
    if (loanPatch.propertyUsageType) {
      loanPatch.applications = [
        {
          id: loan.applications[0].id,
          propertyUsageType: loanPatch.propertyUsageType,
        },
      ];
      delete loanPatch.propertyUsageType;
    }
    await dispatch(updateLoan(loanGuid, loanPatch));
  };

  return (
    <>
      <AutosavingFormProvider
        formMethods={formMethods}
        onSave={handleSave}
        mode={SUBMIT_MODE.onIsPartialValid}
        disabled={disabled}
      >
        <Paper className={styles.form}>
          <div className={clsx(styles.input, 'flex-container')}>
            <LoanPropertyContainer>
              {({ states }) => (
                <ConnectedAddressInput
                  classes={{ inputItem: styles.addressInputItem }}
                  className='flex'
                  keys={{
                    address: 'property.addressLineText',
                    city: 'property.city',
                    county: 'property.county',
                    state: 'property.state',
                    propertyType: 'customFieldsMapped.rtl.propertyType',
                    numUnits: 'property.financedNumberOfUnits',
                    zip: 'property.postalCode',
                  }}
                  rows={[
                    ['address', 'county'],
                    ['city', 'numUnits'],
                    ['state', ''],
                    ['zip', 'propertyType'],
                  ]}
                  required={{
                    address: 'Street is required',
                    city: 'City is required',
                    county: 'County is required',
                    state: 'State is required',
                    zip: 'Zip is required',
                  }}
                  labels={{
                    state: 'State (Loan Officer license required)',
                  }}
                  propertyTypeOptions={FNF_PROPERTY_TYPE_OPTIONS}
                  statesOptions={states}
                  disabled={disabled}
                />
              )}
            </LoanPropertyContainer>
          </div>
          <div className={styles.input}>
            <ConnectedRadioInput
              label='Occupancy'
              name='propertyUsageType'
              disabled={disabled}
              options={PROPERTYUSAGETYPE_OPTIONS}
              row
            />
          </div>
          <div className={styles.input}>
            <ConnectedRadioInput
              label='Purpose of Loan'
              name='property.loanPurposeType'
              disabled={disabled}
              options={LOAN_PURPOSE_OPTIONS_ALL}
              row
            />
          </div>
          <div className={styles.row}>
            <div className={styles.column}>
              {!isRefinance && (
                <div className={styles.input}>
                  {/* if not refi */}
                  <ConnectedCurrencyInput
                    label='Purchase Price'
                    name='purchasePriceAmount'
                    disabled={disabled}
                  />
                </div>
              )}
              {!isConstruction && (
                <div className={styles.input}>
                  <ConnectedCurrencyInput
                    label='Loan Amount'
                    name='borrowerRequestedLoanAmount'
                    disabled={disabled}
                  />
                </div>
              )}
              <div className={styles.input}>
                <ConnectedPercentInput
                  label='Interest Rate'
                  name='requestedInterestRatePercent'
                  disabled={disabled}
                  max={100}
                />
              </div>
              <div className={clsx(styles.input)}>
                <ConnectedCurrencyInput
                  label='Total Fees'
                  name='customFieldsMapped.sterling.closedTotalFees'
                  prefix='$'
                />
              </div>
              <div className={clsx(styles.input)}>
                <ConnectedPlainInput
                  label='Bank Name'
                  name='customFieldsMapped.sterling.closedBrokerBankName'
                />
              </div>
            </div>
            <div className={styles.column}>
              {isRefinance && (
                <>
                  <div className={styles.column}>
                    <div className={styles.input}>
                      <ConnectedDateInput
                        label='What is the Date of Purchase For This Refinance?'
                        name='property.refinancePropertyAcquiredYear'
                        disabled={disabled}
                      />
                    </div>
                    <div className={styles.input}>
                      <ConnectedCurrencyInput
                        label='What was the Purchase Price For This Refinance?'
                        name='property.refinancePropertyOriginalCostAmount'
                        disabled={disabled}
                      />
                    </div>

                    <div className={clsx(styles.input, styles.withIcons)}>
                      <ConnectedCurrencyInput
                        label='Existing Liens To Be Paid Off?'
                        name='property.refinancePropertyExistingLienAmount'
                        disabled={
                          disabled ||
                          !fieldLockData.includes(
                            'loan.property.refinancePropertyExistingLienAmount',
                          )
                        }
                      />
                      <FieldLockDataToggle
                        fieldContractPath={
                          'loan.property.refinancePropertyExistingLienAmount'
                        }
                        name='property.refinancePropertyExistingLienAmount'
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                {isRefinance && (
                  <div className={styles.input}>
                    <ConnectedCurrencyInput
                      label='Value As Is'
                      name='customFieldsMapped.rtl.valueAsIs'
                      disabled={disabled}
                    />
                  </div>
                )}
                {isConstruction && (
                  <>
                    <div className={styles.input}>
                      <ConnectedCurrencyInput
                        label='Budget Amount'
                        name='customFieldsMapped.rtl.renovationCost'
                        disabled={disabled}
                      />
                    </div>
                    <div className={styles.input}>
                      <ConnectedCurrencyInput
                        label='After Renovation Value'
                        name='customFieldsMapped.rtl.afterRenovationValue'
                        disabled={disabled}
                      />
                    </div>
                    <div className={styles.input}>
                      <ConnectedCurrencyInput
                        label='Day 1 Loan Amount'
                        name='customFieldsMapped.rtl.dayOneLoanAmount'
                        disabled={disabled}
                      />
                    </div>
                    <div className={styles.input}>
                      <ConnectedCurrencyInput
                        label='Holdback Amount'
                        name='customFieldsMapped.rtl.disbursedHoldbackAmount'
                        disabled={disabled}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Paper>
      </AutosavingFormProvider>
    </>
  );
};

export default ClosedSterlingBrokerProperty;
